import {
  CalendarDate,
  DateValue,
  getLocalTimeZone,
  isSameDay,
  toCalendarDate,
  today,
} from "@internationalized/date"
import { RangeValue } from "@react-types/shared"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { BookingCalendarDateAvailability } from "ipctg-inflow-types/InFlowDB"
import {
  ReservationDateAvailabilityStatus,
  ReserveCalendarMode,
} from "ipctg-inflow-types/InFlowEnums"
import { useMemo, useState } from "react"
import type { Key } from "react-aria-components"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import PendingComponent from "@/globalComponents/atoms/PendingComponent"
import Text from "@/globalComponents/atoms/Text"
import Calendar, {
  type CalendarDateStatus,
} from "@/globalComponents/molecules/Calendar"
import DateRangeField from "@/globalComponents/molecules/DateRangeField"
import FilterTabs from "@/globalComponents/molecules/FilterTabs"
import { getRouteParamsForDate } from "@/utilities/date"
import {
  calendarAvailabilityQueryOptions,
  calendarsQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { useTheme } from "@/utilities/hooks/useTheme"

import { CheckBackModal } from "./-components/CheckBackModal"
import { NotRequiredModal } from "./-components/NotRequiredModal"
import { SoldOutModal } from "./-components/SoldOutModal"
import styles from "./index.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/"
)({
  staticData: {
    pageName: "Calendar",
  },
  component: RouteComponent,
})

function toCalendarStatus(
  type: ReservationDateAvailabilityStatus
): CalendarDateStatus {
  switch (type) {
    case ReservationDateAvailabilityStatus.Available:
      return "selectable"
    case ReservationDateAvailabilityStatus.NotRequired:
      return "notRequired"
    case ReservationDateAvailabilityStatus.Full:
    case ReservationDateAvailabilityStatus.FullButNotAllConfirmed:
      return "blocked"
    case ReservationDateAvailabilityStatus.ComingSoon:
      return "disabled"
    case ReservationDateAvailabilityStatus.NoInventoryActive:
    case ReservationDateAvailabilityStatus.Closed:
      return "unavailable"
  }
}

/**
 * Maps every day of the month, not just the days the response mentions. The
 * response is authoritative for the month it covers, so a day missing from it
 * has no known availability and is withheld rather than left selectable. This
 * also covers the previous month's entries that keepPreviousData holds while
 * the next month is in flight.
 */
function buildStatusMap(
  availability: Record<string, BookingCalendarDateAvailability> | undefined,
  monthStart: CalendarDate
): Map<string, CalendarDateStatus> {
  const map = new Map<string, CalendarDateStatus>()
  if (!availability) return map

  for (
    let day = monthStart;
    day.month === monthStart.month;
    day = day.add({ days: 1 })
  ) {
    const date = day.toString()
    const status = availability[date]?.availabilityStatus
    map.set(date, (status && toCalendarStatus(status)) || "unavailable")
  }
  return map
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const params = Route.useParams()
  const { t } = useTranslation("reserve")
  const queryClient = useQueryClient()
  const theme = useTheme()
  const { locationInformation } = theme
  const timezoneLocation = locationInformation?.timeZone || getLocalTimeZone()
  const locationID = locationInformation?.locationID || ""
  const { data: calendars } = useSuspenseQuery(
    calendarsQueryOptions({ locationID })
  )
  const calendar = calendars.find(
    (c) => c.bookingCalendarID === params.calendarID
  )
  const calendarDescription = calendar?.metadata.reservationDatesDescription

  const [selectedDate, setSelectedDate] = useState<DateValue | null>(null)
  const [selectedRange, setSelectedRange] =
    useState<RangeValue<DateValue> | null>(null)
  const [highlightedRange, setHighlightedRange] =
    useState<RangeValue<DateValue> | null>(null)
  const [focusedDate, setFocusedDate] = useState<DateValue>(
    () => new CalendarDate(Number(params.year), Number(params.month), 1)
  )

  if (
    focusedDate.year !== Number(params.year) ||
    focusedDate.month !== Number(params.month)
  ) {
    setFocusedDate(
      new CalendarDate(Number(params.year), Number(params.month), 1)
    )
  }
  const [dateMode, setDateMode] = useState<Key>("multi")
  const [dateError, setDateError] = useState<string | undefined>()
  const [openModal, setOpenModal] = useState<
    "checkBack" | "soldOut" | "notRequired" | null
  >(null)

  const todayDate = today(timezoneLocation)
  const currentMonth = `${params.year}-${params.month.padStart(2, "0")}`

  const { data: availability, isFetching } = useQuery({
    ...calendarAvailabilityQueryOptions({
      bookingCalendarID: params.calendarID,
      month: currentMonth,
    }),
    placeholderData: keepPreviousData,
  })

  const offersRange = availability?.calendarMode === ReserveCalendarMode.Range
  const isRangeMode = offersRange && dateMode === "multi"
  const statusMap = useMemo(
    () =>
      buildStatusMap(
        availability?.availability,
        new CalendarDate(Number(params.year), Number(params.month), 1)
      ),
    [availability, params.year, params.month]
  )
  const startDate = isRangeMode ? selectedRange?.start : selectedDate

  const todayMonth = `${todayDate.year}-${String(todayDate.month).padStart(2, "0")}`

  /*
    Today carries a status like any other day, and that status lives in the
    availability of the month holding it, which is not always the month on
    screen. The shortcut reads that month, then hands today to the handler
    the grid uses, which selects it or explains it in a modal.
  */
  const { mutate: selectToday } = useMutation({
    mutationFn: async () => {
      if (todayMonth !== currentMonth) {
        navigateToMonth(todayDate.year, todayDate.month, true)
      }
      const { availability: todayAvailability } =
        await queryClient.ensureQueryData(
          calendarAvailabilityQueryOptions({
            bookingCalendarID: params.calendarID,
            month: todayMonth,
          })
        )
      handleDateChange(
        todayDate,
        buildStatusMap(
          todayAvailability,
          new CalendarDate(todayDate.year, todayDate.month, 1)
        )
      )
    },
  })

  function navigateToMonth(year: number, month: number, replace = false) {
    navigate({
      to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month",
      params: {
        ...params,
        year: String(year),
        month: String(month).padStart(2, "0"),
      },
      replace,
    })
  }

  return (
    <>
      <Heading level={1} as={1} className="center">
        {t("calendar.heading")}
      </Heading>
      <div className={styles.infoSection}>
        {calendarDescription && <Text level={1}>{calendarDescription}</Text>}
        {offersRange && (
          <FilterTabs
            onSelectionChange={handleDateModeChange}
            selectedKey={dateMode}
            aria-label={t("calendar.heading")}
          >
            <FilterTabs.Tab id="single">
              {t("calendar.singleDay")}
            </FilterTabs.Tab>
            <FilterTabs.Tab id="multi">
              {t("calendar.multipleDays")}
            </FilterTabs.Tab>
          </FilterTabs>
        )}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Text level={2} className="semi-bold">
            {t(isRangeMode ? "calendar.selectDates" : "calendar.selectDate")}
          </Text>
          {!isRangeMode && (
            <Button
              buttonType="primary"
              presentation="link"
              style={{ fontSize: "var(--font-size-2)", fontWeight: 400 }}
              onPress={() => selectToday()}
            >
              {t("calendar.today")}
            </Button>
          )}
        </div>
        <DateRangeField
          value={isRangeMode ? highlightedRange || selectedRange : selectedDate}
          placeholder={t(
            isRangeMode
              ? "calendar.selectDatesPlaceholder"
              : "calendar.selectDatePlaceholder"
          )}
        />
        <FieldError>{dateError}</FieldError>
      </div>
      <div className={styles.calendarWrapper}>
        <Card className={styles.calendarCard}>
          {isRangeMode ? (
            <Calendar
              mode="range"
              value={selectedRange}
              focusedValue={focusedDate}
              onFocusChange={handleFocusChange}
              onChange={handleRangeChange}
              onHighlightedRangeChange={setHighlightedRange}
              minValue={todayDate}
              statusMap={statusMap}
            />
          ) : (
            <Calendar
              appearance={offersRange ? "range" : "default"}
              value={selectedDate}
              focusedValue={focusedDate}
              onFocusChange={handleFocusChange}
              onChange={handleDateChange}
              minValue={todayDate}
              statusMap={statusMap}
            />
          )}
        </Card>
        {isFetching && (
          <>
            <div className={styles.calendarOverlay} />
            <div className={styles.calendarSpinner}>
              <PendingComponent variant="block" />
            </div>
          </>
        )}
      </div>
      <Button
        buttonType="primary"
        onPress={() => {
          if (!startDate) {
            setDateError(
              t(
                isRangeMode
                  ? "calendar.selectDatesRequired"
                  : "calendar.selectDateRequired"
              )
            )
            return
          }
          /*
            A range calendar always states both ends of the span, so a single
            day picked here closes on its own start date. Only a start date
            calendar leaves endDate off entirely.
          */
          const rangeEnd = isRangeMode ? selectedRange?.end : startDate
          const endDate = offersRange && rangeEnd ? rangeEnd : null
          navigate({
            to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/rates",
            params: {
              ...params,
              ...getRouteParamsForDate(startDate),
            },
            search: {
              endDate: endDate ? toCalendarDate(endDate).toString() : undefined,
            },
          })
        }}
      >
        {t("calendar.next")}
      </Button>

      <CheckBackModal
        isOpen={openModal === "checkBack"}
        onOpenChange={(open) => !open && setOpenModal(null)}
      />
      <SoldOutModal
        isOpen={openModal === "soldOut"}
        onOpenChange={(open) => !open && setOpenModal(null)}
      />
      <NotRequiredModal
        isOpen={openModal === "notRequired"}
        onOpenChange={(open) => !open && setOpenModal(null)}
      />
    </>
  )

  function handleDateModeChange(key: Key) {
    setDateMode(key)
    setDateError(undefined)

    if (key === "multi") {
      setSelectedRange(
        selectedDate
          ? { start: selectedDate, end: selectedDate.add({ days: 1 }) }
          : null
      )
      return
    }

    /* A range may start on a day that is not selectable on its own. */
    const start = selectedRange?.start
    const status = start && statusMap.get(start.toString())
    const isSelectable = status === undefined || status === "selectable"
    setSelectedDate(start && isSelectable ? start : null)
  }

  function handleFocusChange(date: CalendarDate) {
    setFocusedDate(date)
    const sameMonth =
      String(date.year) === params.year &&
      String(date.month).padStart(2, "0") === params.month
    if (!sameMonth) {
      navigateToMonth(date.year, date.month, true)
    }
  }

  /*
    Range selection skips the status modals: a contiguous range has no single
    day to explain, and range calendars carry no sold out or check back days.
  */
  function handleRangeChange(range: RangeValue<DateValue> | null) {
    /*
      Both clicks landing on one day is a single day reservation, which is the
      other mode, so the range takes the following day.
    */
    setSelectedRange(
      range && isSameDay(range.start, range.end)
        ? { start: range.start, end: range.start.add({ days: 1 }) }
        : range
    )
    setDateError(undefined)
  }

  function handleDateChange(date: DateValue | null, dateStatuses = statusMap) {
    if (date) {
      const status = dateStatuses.get(date.toString())
      /* The grid gives an unavailable day no press at all, so neither does this. */
      if (status === "unavailable") return
      if (status === "disabled") {
        setOpenModal("checkBack")
        return
      }
      if (status === "blocked") {
        setOpenModal("soldOut")
        return
      }
      if (status === "notRequired") {
        setOpenModal("notRequired")
        return
      }
    }
    setSelectedDate(date)
    setDateError(undefined)
  }
}
